/**
 * CallerInfoButton — reusable "pull info about this caller" affordance.
 * Renders a small ⓘ button; clicking opens a modal with the caller's resolved
 * name, operator, line type, location, call history, and last text from
 * /api/callhistory/caller-info/:number. Used wherever a caller number is
 * visible: Call History rows, AI Agent session headers (voice + SMS), the
 * session picker, and incoming-message footers.
 */
import React, { useState } from 'react';
import { authFetch } from '../../auth';
import './CallerInfoButton.css';

const fmtDateTime = (date) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (seconds) => {
  const s = Number(seconds) || 0;
  if (!s) return '0:00';
  const mins = Math.floor(s / 60);
  const secs = Math.round(s % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const isPhoneish = (s) => {
  const d = String(s || '').replace(/[^\d]/g, '');
  return d.length >= 7;
};

const INFO_SVG = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
);

const CallerInfoButton = ({ number, size, className }) => {
  const [state, setState] = useState(null); // { number, loading, error, data }
  const num = String(number || '').trim();
  if (!num || !isPhoneish(num)) return null;

  const open = async () => {
    setState({ number: num, loading: true, error: null, data: null });
    try {
      const res = await authFetch(`/api/callhistory/caller-info/${encodeURIComponent(num)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setState({ number: num, loading: false, error: null, data });
    } catch (err) {
      console.error('Error loading caller info:', err);
      setState({ number: num, loading: false, error: 'Couldn\'t load caller info', data: null });
    }
  };

  const d = state && state.data;
  const line = (d && d.line) || {};

  return (
    <>
      <button
        type="button"
        className={`callerinfo-btn ${size === 'xs' ? 'callerinfo-btn-xs' : ''} ${className || ''}`}
        onClick={(e) => { e.stopPropagation(); open(); }}
        title="Caller info"
      >
        {INFO_SVG}
      </button>

      {state && (
        <div className="callerinfo-modal modal-overlay" onClick={() => setState(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Caller Info</h3>
              <button className="close-btn" onClick={() => setState(null)} aria-label="Close">×</button>
            </div>
            <div className="modal-content">
              {state.loading ? (
                <div className="callerinfo-body"><div className="callerinfo-loading">Looking up caller…</div></div>
              ) : (state.error || !d) ? (
                <div className="callerinfo-body">
                  <div className="callerinfo-number">{state.number}</div>
                  <div className="callerinfo-error">{state.error || 'No information available'}</div>
                </div>
              ) : (
                <div className="callerinfo-body">
                  <div className="callerinfo-name-row">
                    <span className="callerinfo-name">{d.name || 'Unknown caller'}</span>
                    {d.source === 'contact' && <span className="callerinfo-source contact">★ From your contacts</span>}
                    {d.source === 'directory' && <span className="callerinfo-source directory">▤ Directory</span>}
                  </div>
                  <div className="callerinfo-number">{d.number}</div>
                  {d.contact && d.contact.email_address && (
                    <div className="callerinfo-contact">{d.contact.email_address}</div>
                  )}

                  <div className="callerinfo-grid">
                    <div className="callerinfo-row"><label>Operator</label><span>{line.operator || '—'}</span></div>
                    <div className="callerinfo-row"><label>Line type</label><span>{line.lineType || '—'}</span></div>
                    <div className="callerinfo-row"><label>Location</label><span>{line.location || '—'}</span></div>
                  </div>

                  <div className="callerinfo-history">
                    {d.calls.total === 0
                      ? 'No previous calls with this number'
                      : `${d.calls.total} call${d.calls.total === 1 ? '' : 's'} · ${d.calls.missed} missed · ${formatDuration(d.calls.totalDurationSec)} total`}
                    {d.calls.lastCallAt && ` · last ${fmtDateTime(d.calls.lastCallAt)}`}
                  </div>

                  {d.lastMessage && d.lastMessage.body && (
                    <div className="callerinfo-lastmsg">
                      <span className="callerinfo-lastmsg-direction">{d.lastMessage.incoming ? '↙' : '↗'}</span>
                      <span className="callerinfo-lastmsg-body">"{d.lastMessage.body}"</span>
                      {d.lastMessage.at && <span className="callerinfo-lastmsg-at">{fmtDateTime(d.lastMessage.at)}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CallerInfoButton;
